import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import React from "react";
import type { OrDatepickerSize, OrDatepickerValue, OrDatepickerVariant } from "./or-datepicker";

dayjs.extend(customParseFormat);

const DEFAULT_FORMAT = "DD/MM/YYYY";
const DATE_SEGMENT_ORDER = ["day", "month", "year"] as const;

type DateSegment = (typeof DATE_SEGMENT_ORDER)[number];
type DateSegments = Record<DateSegment, string>;
type DateSegmentInputBuffer = {
  digits: string;
  segment: DateSegment | null;
};

const DATE_SEGMENT_LENGTHS: Record<DateSegment, number> = {
  day: 2,
  month: 2,
  year: 4,
};

const DATE_SEGMENT_PLACEHOLDERS: Record<DateSegment, string> = {
  day: "DD",
  month: "MM",
  year: "YYYY",
};

const DATE_SEGMENT_SELECTION_RANGES: Record<DateSegment, readonly [number, number]> = {
  day: [0, 2],
  month: [3, 5],
  year: [6, 10],
};

function createEmptyDateSegments(): DateSegments {
  return {
    day: "",
    month: "",
    year: "",
  };
}

function parseDateValue(value: OrDatepickerValue, format: string): Dayjs | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  if (value instanceof Date) {
    const resolvedValue = dayjs(value);

    return resolvedValue.isValid() ? resolvedValue : null;
  }

  if (typeof value === "number") {
    const numericValue = String(value);

    if (/^\d{8}$/.test(numericValue)) {
      const formattedValue = dayjs(numericValue, "YYYYMMDD", true);

      if (formattedValue.isValid()) {
        return formattedValue;
      }
    }

    const resolvedValue = dayjs(value);

    return resolvedValue.isValid() ? resolvedValue : null;
  }

  if (typeof value === "string") {
    const formattedValue = dayjs(value, format, true);

    if (formattedValue.isValid()) {
      return formattedValue;
    }

    const isoValue = dayjs(value);

    return isoValue.isValid() ? isoValue : null;
  }

  return null;
}

function buildCalendarDays(
  monthCursor: Dayjs,
  minDate: Dayjs | null,
  maxDate: Dayjs | null,
  selectedDate: Dayjs | null,
) {
  const firstDayOfMonth = monthCursor.startOf("month");
  const offset = (firstDayOfMonth.day() + 6) % 7;
  const calendarStart = firstDayOfMonth.subtract(offset, "day");

  return Array.from({ length: 42 }, (_value, index) => {
    const date = calendarStart.add(index, "day");
    const isBeforeMin = minDate ? date.isBefore(minDate, "day") : false;
    const isAfterMax = maxDate ? date.isAfter(maxDate, "day") : false;

    return {
      date,
      isCurrentMonth: date.month() === monthCursor.month(),
      isDisabled: isBeforeMin || isAfterMax,
      isSelected: selectedDate ? date.isSame(selectedDate, "day") : false,
      isToday: date.isSame(dayjs(), "day"),
    };
  });
}

function getDateSegments(value: Dayjs | null): DateSegments {
  if (!value) {
    return createEmptyDateSegments();
  }

  return {
    day: value.format("DD"),
    month: value.format("MM"),
    year: value.format("YYYY"),
  };
}

function hasAnyDateSegmentValue(segments: DateSegments): boolean {
  return DATE_SEGMENT_ORDER.some((segment) => segments[segment].length > 0);
}

function areDateSegmentsComplete(segments: DateSegments): boolean {
  return DATE_SEGMENT_ORDER.every((segment) => segments[segment].length === DATE_SEGMENT_LENGTHS[segment]);
}

function formatDateSegments(segments: DateSegments, showPlaceholders: boolean): string {
  if (!showPlaceholders && !hasAnyDateSegmentValue(segments)) {
    return "";
  }

  const day = showPlaceholders
    ? `${segments.day}${DATE_SEGMENT_PLACEHOLDERS.day.slice(segments.day.length)}`
    : segments.day;
  const month = showPlaceholders
    ? `${segments.month}${DATE_SEGMENT_PLACEHOLDERS.month.slice(segments.month.length)}`
    : segments.month;
  const year = showPlaceholders
    ? `${segments.year}${DATE_SEGMENT_PLACEHOLDERS.year.slice(segments.year.length)}`
    : segments.year;

  return `${day}/${month}/${year}`;
}

function parseInputToDateSegments(value: string): DateSegments {
  const digits = value.replace(/\D/g, "").slice(0, 8);

  return {
    day: digits.slice(0, 2),
    month: digits.slice(2, 4),
    year: digits.slice(4, 8),
  };
}

function getDateSegment(selectionStart: number | null, fallbackSegment: DateSegment): DateSegment {
  if (selectionStart === null) {
    return fallbackSegment;
  }

  if (selectionStart <= 2) {
    return "day";
  }

  if (selectionStart <= 5) {
    return "month";
  }

  return "year";
}

function selectDateSegment(input: HTMLInputElement, segment: DateSegment): void {
  const [start, end] = DATE_SEGMENT_SELECTION_RANGES[segment];
  input.setSelectionRange(start, end);
}

function getAdjacentDateSegment(segment: DateSegment, direction: -1 | 1): DateSegment {
  const currentIndex = DATE_SEGMENT_ORDER.indexOf(segment);
  const nextIndex = Math.min(Math.max(currentIndex + direction, 0), DATE_SEGMENT_ORDER.length - 1);

  return DATE_SEGMENT_ORDER[nextIndex];
}

function clearDateSegmentValue(segments: DateSegments, segment: DateSegment): DateSegments {
  return {
    ...segments,
    [segment]: "",
  };
}

function resolveDateSegmentInput(
  segments: DateSegments,
  segment: DateSegment,
  digit: string,
  currentBuffer: DateSegmentInputBuffer,
): { nextBuffer: DateSegmentInputBuffer; nextSegments: DateSegments } {
  const maxLength = DATE_SEGMENT_LENGTHS[segment];
  const previousDigits = currentBuffer.segment === segment ? currentBuffer.digits : "";
  const nextDigits = `${previousDigits}${digit}`.slice(0, maxLength);

  return {
    nextBuffer: {
      digits: nextDigits.length >= maxLength ? "" : nextDigits,
      segment,
    },
    nextSegments: {
      ...segments,
      [segment]: nextDigits,
    },
  };
}

export interface UseOrDatepickerParams {
  className?: string;
  disabled?: boolean;
  format?: string;
  fullWidth?: boolean;
  hasError?: boolean;
  interactiveRefs?: readonly { readonly current: HTMLElement | null }[];
  maxDate?: OrDatepickerValue;
  minDate?: OrDatepickerValue;
  onChange?: (nextValue: Dayjs | null) => void;
  size?: OrDatepickerSize;
  value?: OrDatepickerValue;
  variant?: OrDatepickerVariant;
}

export interface UseOrDatepickerResult {
  calendarDays: ReturnType<typeof buildCalendarDays>;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  handleDayClick: (day: Dayjs) => void;
  handleInputBlur: () => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputClick: (event: React.MouseEvent<HTMLInputElement>) => void;
  handleInputFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handleInputSelect: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  handleNextMonth: () => void;
  handlePreviousMonth: () => void;
  handleToggleCalendar: () => void;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  inputValue: string;
  isOpen: boolean;
  monthLabel: string;
  selectedDate: Dayjs | null;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wrapperClassName: string;
}

export function useOrDatepicker({
  className,
  disabled = false,
  format = DEFAULT_FORMAT,
  fullWidth = true,
  hasError = false,
  interactiveRefs = [],
  maxDate,
  minDate,
  onChange,
  size = "md",
  value,
  variant = "default",
}: UseOrDatepickerParams): UseOrDatepickerResult {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const pendingSelectionSegmentRef = React.useRef<DateSegment | null>(null);
  const dateSegmentInputBufferRef = React.useRef<DateSegmentInputBuffer>({ digits: "", segment: null });
  const selectedDate = React.useMemo(() => parseDateValue(value ?? null, format), [format, value]);
  const minDateValue = React.useMemo(() => parseDateValue(minDate ?? null, format), [format, minDate]);
  const maxDateValue = React.useMemo(() => parseDateValue(maxDate ?? null, format), [format, maxDate]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [activeSegment, setActiveSegment] = React.useState<DateSegment>("day");
  const [inputSegments, setInputSegments] = React.useState<DateSegments>(() => getDateSegments(selectedDate));
  const [monthCursor, setMonthCursor] = React.useState(selectedDate?.startOf("month") ?? dayjs().startOf("month"));
  const isWithinInteractiveRegion = React.useCallback(
    (target: EventTarget | Node | null): boolean => {
      if (!(target instanceof Node)) {
        return false;
      }

      if (containerRef.current?.contains(target)) {
        return true;
      }

      return interactiveRefs.some((ref) => ref.current?.contains(target));
    },
    [interactiveRefs],
  );

  React.useEffect(() => {
    setInputSegments(getDateSegments(selectedDate));

    if (selectedDate) {
      setMonthCursor(selectedDate.startOf("month"));
    }
  }, [selectedDate]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (!isWithinInteractiveRegion(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isOpen, isWithinInteractiveRegion]);

  const syncDateSegmentSelection = React.useCallback((segment: DateSegment): void => {
    pendingSelectionSegmentRef.current = segment;

    if (!inputRef.current || document.activeElement !== inputRef.current) {
      return;
    }

    selectDateSegment(inputRef.current, segment);
  }, []);

  React.useLayoutEffect(() => {
    if (!inputRef.current || document.activeElement !== inputRef.current || !pendingSelectionSegmentRef.current) {
      return;
    }

    selectDateSegment(inputRef.current, pendingSelectionSegmentRef.current);
    pendingSelectionSegmentRef.current = null;
  }, [activeSegment, inputSegments, isFocused]);

  const handleDateSegmentSelection = React.useCallback(
    (input: HTMLInputElement, segment: DateSegment): void => {
      dateSegmentInputBufferRef.current = { digits: "", segment };
      setActiveSegment(segment);
      syncDateSegmentSelection(segment);

      if (document.activeElement === input) {
        selectDateSegment(input, segment);
      }
    },
    [syncDateSegmentSelection],
  );

  const commitInputSegments = React.useCallback(
    (segments: DateSegments): void => {
      if (!hasAnyDateSegmentValue(segments)) {
        setInputSegments(createEmptyDateSegments());
        onChange?.(null);
        return;
      }

      if (!areDateSegmentsComplete(segments)) {
        setInputSegments(getDateSegments(selectedDate));
        return;
      }

      const parsedValue = dayjs(
        `${segments.day}/${segments.month}/${segments.year}`,
        format,
        true,
      );

      if (!parsedValue.isValid()) {
        setInputSegments(getDateSegments(selectedDate));
        return;
      }

      if (minDateValue && parsedValue.isBefore(minDateValue, "day")) {
        setInputSegments(getDateSegments(selectedDate));
        return;
      }

      if (maxDateValue && parsedValue.isAfter(maxDateValue, "day")) {
        setInputSegments(getDateSegments(selectedDate));
        return;
      }

      const normalizedValue = parsedValue.startOf("day");
      setInputSegments(getDateSegments(normalizedValue));
      setMonthCursor(normalizedValue.startOf("month"));
      onChange?.(normalizedValue);
    },
    [format, maxDateValue, minDateValue, onChange, selectedDate],
  );

  const handleDayClick = React.useCallback(
    (day: Dayjs): void => {
      if (disabled) {
        return;
      }

      if ((minDateValue && day.isBefore(minDateValue, "day")) || (maxDateValue && day.isAfter(maxDateValue, "day"))) {
        return;
      }

      const normalizedValue = day.startOf("day");
      setInputSegments(getDateSegments(normalizedValue));
      setMonthCursor(normalizedValue.startOf("month"));
      setIsOpen(false);
      onChange?.(normalizedValue);
    },
    [disabled, maxDateValue, minDateValue, onChange],
  );

  const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    setInputSegments(parseInputToDateSegments(event.currentTarget.value));
  }, []);

  const handleInputFocus = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      if (disabled) {
        return;
      }

      setIsFocused(true);
      handleDateSegmentSelection(event.currentTarget, "day");
    },
    [disabled, handleDateSegmentSelection],
  );

  const handleInputClick = React.useCallback(
    (event: React.MouseEvent<HTMLInputElement>): void => {
      if (disabled) {
        return;
      }

      handleDateSegmentSelection(event.currentTarget, getDateSegment(event.currentTarget.selectionStart, activeSegment));
    },
    [activeSegment, disabled, handleDateSegmentSelection],
  );

  const handleInputSelect = React.useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>): void => {
      setActiveSegment(getDateSegment(event.currentTarget.selectionStart, activeSegment));
    },
    [activeSegment],
  );

  const handleInputBlur = React.useCallback((): void => {
    setIsFocused(false);
    dateSegmentInputBufferRef.current = { digits: "", segment: null };
    commitInputSegments(inputSegments);
  }, [commitInputSegments, inputSegments]);

  const handleToggleCalendar = React.useCallback((): void => {
    if (disabled) {
      return;
    }

    setMonthCursor((selectedDate ?? dayjs()).startOf("month"));
    setIsOpen((currentValue) => !currentValue);
  }, [disabled, selectedDate]);

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      const currentSegment = activeSegment;

      if (event.key === "Escape") {
        setIsOpen(false);
        return;
      }

      if (event.key === "Enter") {
        commitInputSegments(inputSegments);
        setIsOpen(false);
        return;
      }

      if (disabled) {
        return;
      }

      if (event.key === "Tab") {
        const canMoveForward = currentSegment !== "year";
        const canMoveBackward = currentSegment !== "day";

        if (!event.shiftKey && canMoveForward) {
          event.preventDefault();
          handleDateSegmentSelection(event.currentTarget, getAdjacentDateSegment(currentSegment, 1));
        }

        if (event.shiftKey && canMoveBackward) {
          event.preventDefault();
          handleDateSegmentSelection(event.currentTarget, getAdjacentDateSegment(currentSegment, -1));
        }

        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        handleDateSegmentSelection(event.currentTarget, getAdjacentDateSegment(currentSegment, -1));
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        handleDateSegmentSelection(event.currentTarget, getAdjacentDateSegment(currentSegment, 1));
        return;
      }

      if (event.key === "/") {
        event.preventDefault();

        if (currentSegment !== "year") {
          handleDateSegmentSelection(event.currentTarget, getAdjacentDateSegment(currentSegment, 1));
        }

        return;
      }

      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        dateSegmentInputBufferRef.current = { digits: "", segment: currentSegment };
        setInputSegments((currentValue) => clearDateSegmentValue(currentValue, currentSegment));
        syncDateSegmentSelection(currentSegment);
        return;
      }

      if (/^\d$/.test(event.key) && !event.altKey && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();

        const { nextBuffer, nextSegments } = resolveDateSegmentInput(
          inputSegments,
          currentSegment,
          event.key,
          dateSegmentInputBufferRef.current,
        );

        dateSegmentInputBufferRef.current = nextBuffer;
        setInputSegments(nextSegments);
        setActiveSegment(currentSegment);
        syncDateSegmentSelection(currentSegment);
      }
    },
    [activeSegment, commitInputSegments, disabled, handleDateSegmentSelection, inputSegments, syncDateSegmentSelection],
  );

  const calendarDays = React.useMemo(
    () => buildCalendarDays(monthCursor, minDateValue, maxDateValue, selectedDate),
    [maxDateValue, minDateValue, monthCursor, selectedDate],
  );

  const wrapperClassName = [
    "or-datepicker",
    `or-datepicker--${variant}`,
    `or-datepicker--${size}`,
    hasError ? "or-datepicker--error" : "",
    fullWidth ? "or-datepicker--full-width" : "",
    disabled ? "or-datepicker--disabled" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return {
    calendarDays,
    containerRef,
    handleDayClick,
    handleInputBlur,
    handleInputChange,
    handleInputClick,
    handleInputFocus,
    handleInputKeyDown,
    handleInputSelect,
    handleNextMonth: () => setMonthCursor((currentValue) => currentValue.add(1, "month")),
    handlePreviousMonth: () => setMonthCursor((currentValue) => currentValue.subtract(1, "month")),
    handleToggleCalendar,
    inputRef,
    inputValue: formatDateSegments(inputSegments, isFocused),
    isOpen,
    monthLabel: monthCursor.locale("es").format("MMMM YYYY"),
    selectedDate,
    setIsOpen,
    wrapperClassName,
  };
}
